import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Container } from 'react-bootstrap';
import { useLenis } from 'lenis/react';
import usePageMeta from '../../hooks/usePageMeta';
import type { LegalDoc } from './legalContent';
import styles from './LegalPage.module.css';

const num = (i: number) => String(i + 1).padStart(2, '0');

/**
 * Shared layout for /terms and /privacy: a dark side panel with the title and
 * a numbered contents list (the section you're reading is highlighted), and
 * the full text in a white reading column. On phones the panel sits on top.
 */
const LegalPage: React.FC<{ doc: LegalDoc }> = ({ doc }) => {
  usePageMeta({
    title: doc.metaTitle,
    description: doc.metaDescription,
    url: `https://www.saharicargo.com/${doc.slug}`,
  });

  const lenis = useLenis();
  const [current, setCurrent] = useState(doc.sections[0]?.id);

  // Highlight the section that's in the upper part of the screen.
  useEffect(() => {
    const els = doc.sections
      .map((s) => document.getElementById(`${doc.slug}-${s.id}`))
      .filter((el): el is HTMLElement => !!el);
    const io = new IntersectionObserver(
      (entries) => {
        const hit = entries.find((e) => e.isIntersecting);
        if (hit) setCurrent(hit.target.id.replace(`${doc.slug}-`, ''));
      },
      { rootMargin: '-25% 0px -65% 0px' },
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [doc]);

  const goTo = (e: React.MouseEvent, id: string) => {
    const el = document.getElementById(`${doc.slug}-${id}`);
    if (!el) return;
    e.preventDefault();
    const header = parseFloat(
      getComputedStyle(document.documentElement).getPropertyValue('--header-h'),
    ) || 96;
    if (lenis) lenis.scrollTo(el, { offset: -(header + 16) });
    else el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setCurrent(id);
  };

  return (
    <section className={styles.page}>
      <Container fluid="xl" className={styles.inner}>
        <div className={styles.grid}>
          <aside className={styles.side}>
            <p className={styles.eyebrow}>Legal</p>
            <h1 className={styles.title}>{doc.title}</h1>
            <p className={styles.updated}>Last updated: {doc.updated}</p>
            <nav aria-label={`${doc.title} contents`}>
              <ol className={styles.toc}>
                {doc.sections.map((s, i) => (
                  <li key={s.id}>
                    <a
                      href={`#${doc.slug}-${s.id}`}
                      onClick={(e) => goTo(e, s.id)}
                      className={`${styles.tocLink} ${current === s.id ? styles.tocOn : ''}`}
                      aria-current={current === s.id ? 'true' : undefined}
                    >
                      <span className={styles.tocNum}>{num(i)}</span>
                      {s.title}
                    </a>
                  </li>
                ))}
              </ol>
            </nav>
            <p className={styles.other}>
              {doc.slug === 'terms' ? (
                <>
                  See also our <Link to="/privacy">Privacy Policy</Link>
                </>
              ) : (
                <>
                  See also our <Link to="/terms">Terms &amp; Conditions</Link>
                </>
              )}
            </p>
          </aside>

          <article className={styles.doc}>
            <p className={styles.lead}>{doc.lead}</p>
            {doc.sections.map((s, i) => (
              <section key={s.id} id={`${doc.slug}-${s.id}`} className={styles.section}>
                <h2 className={styles.h2}>
                  <span className={styles.h2Num}>{num(i)}</span>
                  {s.title}
                </h2>
                {s.paragraphs?.map((p) => (
                  <p key={p} className={styles.p}>
                    {p}
                  </p>
                ))}
                {s.bullets && (
                  <ul className={styles.ul}>
                    {s.bullets.map((b) => (
                      <li key={b}>{b}</li>
                    ))}
                  </ul>
                )}
              </section>
            ))}
          </article>
        </div>
      </Container>
    </section>
  );
};

export default LegalPage;
